/* Session et profil. Le niveau d'accès vient de la table `profils`,
   jamais d'un champ que le navigateur pourrait fabriquer. */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { jetonsDeLien, nettoyerAdresse, supabase } from "@/lib/supabase";
import { colonneAbsente } from "@/lib/donnees";
import type { NiveauAgent, Profil } from "@/types/metier";

interface EtatSession {
  session: Session | null;
  profil: Profil | null;
  chargement: boolean;
  erreur: string | null;
  /** Le niveau qui commande l'écran : le vrai, ou celui qu'on regarde en aperçu. */
  niveau: NiveauAgent;
  /**
   * Non nul quand la direction regarde l'application comme un agent.
   *
   * Ce n'est qu'un aperçu de l'INTERFACE : la base continue de répondre avec
   * les droits du compte connecté. Pour éprouver ce qu'un agent reçoit
   * réellement, il faut un vrai compte agent — l'écran le dit en clair.
   */
  apercu: NiveauAgent | null;
  voirComme: (n: NiveauAgent | null) => void;
  connexion: (email: string, motDePasse: string) => Promise<void>;
  deconnexion: () => Promise<void>;
}

const Ctx = createContext<EtatSession | null>(null);

const RANG: Record<NiveauAgent, number> = { agent: 0, admin: 1 };

export function autorise(niveau: NiveauAgent | undefined, requis: NiveauAgent): boolean {
  if (!niveau) return false;
  return RANG[niveau] >= RANG[requis];
}

/**
 * Le niveau tel que l'écran le comprend.
 *
 * Les anciens noms — junior, senior — ont fusionné en « agent ». La base est
 * renommée par migration, mais l'application ne doit pas dépendre de l'ordre
 * des deux déploiements : si elle part avant la migration, une fiche encore
 * marquée « senior » tomberait dans aucun cas, et tout se fermerait pour son
 * porteur. On traduit donc l'étiquette, sans rien accorder de plus : seule la
 * direction reste la direction, et c'est la base qui en décide.
 */
export function normaliser(niveau: string | null | undefined): NiveauAgent | undefined {
  if (niveau === "admin") return "admin";
  if (niveau === "agent" || niveau === "senior" || niveau === "junior") return "agent";
  return undefined;
}

/* L'aperçu tient dans l'onglet et pas au-delà : fermer l'onglet le lève, et
   un rechargement en cours d'essai ne fait pas perdre la vue qu'on observait. */
const CLE_APERCU = "omnix.apercu";

function apercuRange(): NiveauAgent | null {
  try {
    const v = sessionStorage.getItem(CLE_APERCU);
    return v === "agent" || v === "admin" ? v : null;
  } catch {
    return null;
  }
}

export function FournisseurSession({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profil, setProfil] = useState<Profil | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [demande, setDemande] = useState<NiveauAgent | null>(apercuRange);

  useEffect(() => {
    let vivant = true;

    /* Un lien d'invitation ou de réinitialisation apporte ses jetons : on ouvre
       la session avec, puis on efface le fragment de l'adresse. Sans jetons,
       on reprend simplement la session déjà en place. */
    const ouvrir = async () => {
      if (jetonsDeLien) {
        const { data, error } = await supabase.auth.setSession({
          access_token: jetonsDeLien.acces,
          refresh_token: jetonsDeLien.rafraichissement,
        });
        nettoyerAdresse();
        if (!error && data.session) return data.session;
      }
      const { data } = await supabase.auth.getSession();
      return data.session;
    };

    void ouvrir().then((s) => {
      if (!vivant) return;
      setSession(s);
      if (!s) setChargement(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (!s) {
        setProfil(null);
        setChargement(false);
      }
    });
    return () => {
      vivant = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) return;
    let vivant = true;
    setChargement(true);
    /* La fiche agent existe avant le compte : elle est importée, puis invitée.
       À la première connexion, elle n'est donc rattachée à personne — on la
       réclame, par l'adresse vérifiée du jeton. La fonction n'écrit que le
       rattachement, et ne fait rien si quelqu'un l'a déjà prise. */
    const charger = async () => {
      const lire = () =>
        supabase
          .from("profils")
          .select("id, nom, email, niveau, actif, compte_id, invite_le")
          .eq("compte_id", session.user.id)
          .maybeSingle();

      let { data, error } = await lire();

      /* Base pas encore migrée : `compte_id` n'existe pas, la fiche porte
         encore l'identifiant du compte. On lit à l'ancienne plutôt que de
         laisser l'utilisateur devant « compte sans profil ». */
      if (colonneAbsente(error)) {
        return supabase
          .from("profils")
          .select("id, nom, email, niveau, actif")
          .eq("id", session.user.id)
          .maybeSingle();
      }

      if (!error && !data) {
        const { error: e } = await supabase.rpc("reclamer_fiche");
        if (!e) ({ data, error } = await lire());
      }
      return { data, error };
    };

    void charger().then(({ data, error }) => {
      if (!vivant) return;
      if (error) setErreur(error.message);
      /* L'étiquette du niveau est traduite une fois, ici, à l'entrée : le
         reste de l'application ne connaît que les deux noms en service. */
      const f = data as Profil | null;
      setProfil(f ? { ...f, niveau: normaliser(f.niveau) ?? "agent" } : null);
      setChargement(false);
    });
    return () => {
      vivant = false;
    };
  }, [session]);

  /* L'aperçu n'est offert qu'à la direction, et il ne peut que descendre :
     un compte agent qui écrirait « admin » dans son navigateur ne gagnerait
     rien — ni ici, où la condition le refuse, ni dans la base, qui ne lit pas
     le navigateur. */
  const apercu =
    profil?.niveau === "admin" && demande !== null && demande !== "admin" ? demande : null;
  const niveau = apercu ?? profil?.niveau ?? "agent";

  const valeur = useMemo<EtatSession>(
    () => ({
      session,
      profil,
      chargement,
      erreur,
      niveau,
      apercu,
      voirComme(n) {
        setDemande(n);
        try {
          if (n) sessionStorage.setItem(CLE_APERCU, n);
          else sessionStorage.removeItem(CLE_APERCU);
        } catch {
          /* Navigation privée : l'aperçu vivra le temps de la page, c'est tout. */
        }
      },
      async connexion(email, motDePasse) {
        setErreur(null);
        const { error } = await supabase.auth.signInWithPassword({
          email: email.trim(),
          password: motDePasse,
        });
        if (error) {
          /* Une clé refusée n'est pas un mauvais mot de passe : le dire, sinon
             on cherche du côté du compte pendant que la faute est ailleurs. */
          setErreur(
            error.status === 401
              ? "Le serveur refuse la clé de l'application : sa configuration est à reprendre."
              : "Identifiants refusés.",
          );
          throw error;
        }
      },
      async deconnexion() {
        await supabase.auth.signOut();
        setProfil(null);
      },
    }),
    [session, profil, chargement, erreur, niveau, apercu],
  );

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

export function useSession(): EtatSession {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSession doit être utilisé dans FournisseurSession");
  return c;
}

/**
 * Le niveau qui commande l'écran. C'est celui du compte, sauf quand la
 * direction regarde l'application en aperçu — et c'est voulu : les écrans
 * n'ont pas à savoir qu'un aperçu existe.
 */
export function useNiveau(): NiveauAgent {
  return useSession().niveau;
}
